// src/services/demoAdminRepair.js
//
// Self-heal for the demo admin (admin@etqan.demo).
// Called from Login's demo tile on first login AND from Layout when an
// already-signed-in admin lands with tenantId=null (typically because
// their user doc was wiped by an older seed). Relies on the
// self-bootstrap branches in firestore.rules — see `users` update and
// `tenants` update rules for the exact conditions.

import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ROLES } from './userService';

export const DEMO_ADMIN_EMAIL = 'admin@etqan.demo';
export const DEMO_TENANT_ID = 'etqan-madrasa';

const DEMO_TENANT_DOC = {
  name: 'مدرسة الإتقان لتحفيظ القرآن الكريم',
  location: 'الدار البيضاء، المغرب',
  plan: 'pro',
  primaryColor: '#1E6F5C',
  logoUrl: null,
  locale: 'ar',
  studentLimit: 100,
  teacherLimit: 8,
};

const DEMO_ADMIN_DISPLAY_NAME = 'الشيخ عبد الرحمن الإدريسي';

/**
 * Ensure tenants/etqan-madrasa and users/{uid} are in the expected shape
 * for the demo admin. Idempotent. Throws with a specific message when a
 * state can't be reached from the client (e.g. the tenant is already
 * owned by a different auth user).
 */
const step = async (label, fn) => {
  try {
    const result = await fn();
    console.info(`[demo-repair] ${label}: ok`);
    return result;
  } catch (err) {
    console.error(`[demo-repair] ${label} failed:`, err?.code || err?.message || err);
    const wrapped = new Error(`${label}: ${err?.code || err?.message || 'unknown'}`);
    wrapped.code = err?.code;
    wrapped.originalError = err;
    throw wrapped;
  }
};

export const repairDemoAdminProfile = async (user) => {
  if (!user?.uid) throw new Error('repairDemoAdminProfile: no signed-in user');
  console.info('[demo-repair] starting for uid', user.uid);

  const tenantRef = doc(db, 'tenants', DEMO_TENANT_ID);
  const tenantSnap = await step('tenant.read', () => getDoc(tenantRef).catch((err) => {
    // Read may be denied if the doc exists but we aren't the owner yet.
    // Treat that as "unknown state" and let the write path decide.
    if (err?.code === 'permission-denied') return null;
    throw err;
  }));

  if (!tenantSnap || !tenantSnap.exists()) {
    await step('tenant.create', () => setDoc(tenantRef, {
      ...DEMO_TENANT_DOC,
      ownerUid: user.uid,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    }));
  } else {
    const tenantData = tenantSnap.data() || {};
    if (tenantData.ownerUid !== user.uid) {
      await step('tenant.claim', () => updateDoc(tenantRef, {
        ownerUid: user.uid,
        updatedAt: Timestamp.now(),
      }));
    }
  }

  const userRef = doc(db, 'users', user.uid);
  const snap = await step('user.read', () => getDoc(userRef));

  if (!snap.exists()) {
    await step('user.create', () => setDoc(userRef, {
      role: ROLES.ADMIN,
      tenantId: DEMO_TENANT_ID,
      email: user.email || DEMO_ADMIN_EMAIL,
      displayName: user.displayName || DEMO_ADMIN_DISPLAY_NAME,
      linkedStudentIds: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    }));
    return;
  }

  const data = snap.data() || {};
  if (data.tenantId === DEMO_TENANT_ID && data.role === ROLES.ADMIN) {
    console.info('[demo-repair] user doc already healthy');
    return;
  }

  await step('user.patch', () => updateDoc(userRef, {
    role: ROLES.ADMIN,
    tenantId: DEMO_TENANT_ID,
    email: user.email || DEMO_ADMIN_EMAIL,
    displayName: data.displayName || user.displayName || DEMO_ADMIN_DISPLAY_NAME,
    updatedAt: Timestamp.now(),
  }));
};

export default repairDemoAdminProfile;
